import React, { useMemo } from "react";
import { Alert } from "react-bootstrap";
import { FiltersBar } from "./FiltersBar";
import { DataTable } from "./DataTable";
import { ReportToolbar } from "./ReportToolbar";
import { useCSReportRunner } from "./useCSReportRunner";
import {
  downloadSummaryByTeacherCSV,
  fetchSummaryByTeacher,
} from "./csReportsApi";
import {
  buildReportParams,
  explicitColumns,
  validateBaseFilters,
} from "./reportHelpers";

const COLUMNS_BASE = [
  ["created_date", "Date"],
  ["teacher_selection", "Standard"],
  ["educator_count", "Educator Count"],
  ["session_count", "Session Count"],
  ["student_count", "Student Count"],
  ["session_duration", "Session Duration (capped, min)"],
  ["total_session_duration", "Total Session Duration (min)"],
];

const COLUMNS_FULL = [
  ["teacher_email", "Teacher"],
  ...COLUMNS_BASE,
];

export const StandardsListTab = ({
  filters,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const runner = useCSReportRunner({
    fetchJson: fetchSummaryByTeacher,
    fetchCsv: downloadSummaryByTeacherCSV,
    errorTag: "standards-list",
  });

  const validation = useMemo(
    () => validateBaseFilters({ filters, startDate, endDate }),
    [filters, startDate, endDate]
  );

  const params = useMemo(() => {
    if (!validation.ok) return null;
    return {
      ...buildReportParams({ filters, startDate, endDate }),
      roll_up: "session_list",
    };
  }, [filters, startDate, endDate, validation.ok]);

  const columns = useMemo(() => {
    const specs = filters.teacher ? COLUMNS_BASE : COLUMNS_FULL;
    return explicitColumns(specs, runner.rows);
  }, [filters.teacher, runner.rows]);

  return (
    <div>
      <FiltersBar
        filters={filters}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
      />

      <ReportToolbar
        validation={validation}
        loading={runner.loading}
        downloading={runner.downloading}
        onRun={() => runner.run(params)}
        onDownload={() => runner.download(params)}
      />

      {runner.error && <Alert variant="danger">{String(runner.error)}</Alert>}

      {runner.rows !== null && (
        <DataTable
          data={runner.rows}
          columns={columns}
          emptyMessage="No sessions in this date range for the selected filters."
        />
      )}
    </div>
  );
};
